import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactComponent as View } from 'assets/images/ic_view.svg';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { ReactComponent as Maturity } from 'assets/images/ic_maturity.svg';
import { ReactComponent as Check } from 'assets/images/ic_check.svg';
import TimelineIcon from '@mui/icons-material/Timeline';
import { AIMaturityService } from 'services/AIMaturityService';
import { useNavigate } from 'react-router-dom';
import { AppPrimaryButtonOutlined } from 'shared/components/AppComponents';

export interface StageData {
  _id: string;
  stage_name: string;
  stage_number: number;
  total_checklist: number;
  completed_count: number;
  percentage: number;
}

export interface MaturitySummary {
  total_checklist: number;
  completed_count: number;
  percentage: number;
  next_stage_indicator: string;
  next_stage_name: string;
  status: string;
  isLoaded: boolean;
  stagewise: StageData[];
}

interface AIMaturityAssessmentProps {
  minimize: boolean;
}

const AIMaturityAssessment = ({
  minimize,
}: AIMaturityAssessmentProps): React.ReactElement => {
  const [maturitySummary, setMaturitySummary] = useState<MaturitySummary>({
    total_checklist: 0,
    completed_count: 0,
    percentage: 0,
    next_stage_indicator: '',
    next_stage_name: '',
    status: '',
    isLoaded: false,
    stagewise: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // API Calls
  const loadMaturitySummary = (): void => {
    setIsLoading(true);
    AIMaturityService.getSummary()
      .then((response) => {
        const { data } = response.data;
        setMaturitySummary({
          ...data.overall,
          next_stage_indicator: data.next_stage_indicator,
          next_stage_name: data.next_stage_name,
          status: data.status,
          stagewise: data.stagewise,
          isLoaded: true,
        });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Hooks
  useEffect(() => {
    loadMaturitySummary();
  }, []);

  return (
    <Card
      className="maturity-card"
      variant="outlined"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {!minimize && (
        <CardHeader
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2.5 },
            borderBottom: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
            gap: 1.5,
            '& .MuiCardHeader-content': { flex: '1 1 auto', minWidth: 0 },
            '& .MuiCardHeader-action': {
              m: 0,
              width: { xs: '100%', sm: 'auto' },
            },
          }}
          title={
            <Stack direction="row" alignItems="center" gap={1}>
              <TimelineIcon fontSize="small" />
              <Typography variant="body1" className="fw-8">
                AI Maturity Assessment
              </Typography>
            </Stack>
          }
          action={
            <AppPrimaryButtonOutlined
              startIcon={
                <SvgIcon
                  component={View}
                  inheritViewBox
                  fontSize="inherit"
                  htmlColor="#76A81F"
                />
              }
              className="view-btn btn-extra-small"
              title="View full assessment"
              onClick={() => navigate('/ai-maturity')}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          }
        />
      )}

      {isLoading ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <CardContent
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: { xs: 2, sm: 2.5 },
              '&:last-child': { pb: 2 },
            }}
          >
            <Stack spacing={0.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <SvgIcon
                    component={Maturity}
                    inheritViewBox
                    fontSize="inherit"
                    htmlColor="#052921"
                  />
                  <Typography className="text-green">
                    AI Maturity Progress
                  </Typography>
                </Stack>
                <Typography className="text-green">
                  {maturitySummary.percentage}%
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Overall progress
              </Typography>

              <Stack direction="row" alignItems="center" gap={1.25} mt={0.75}>
                <LinearProgress
                  className="maturity-progress-bar"
                  variant="determinate"
                  value={maturitySummary.percentage}
                  sx={{ flex: 1 }}
                />
                <Typography
                  className="text-muted"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    minWidth: 32,
                    textAlign: 'right',
                  }}
                >
                  {maturitySummary.percentage}%
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {maturitySummary.completed_count} of{' '}
                {maturitySummary.total_checklist} items completed
              </Typography>
            </Stack>

            <Stack
              className="stage-journey"
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              gap={{ xs: 2, sm: 1 }}
              sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5 }}
            >
              <Box sx={{ flexShrink: 0, minWidth: { sm: 130 } }}>
                <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
                  <SvgIcon
                    component={Check}
                    inheritViewBox
                    fontSize="inherit"
                  />
                  <Typography color="#d1f27a" fontWeight={500} fontSize={15}>
                    Current Stage
                  </Typography>
                </Stack>
                <Typography variant="h6" className="fw-8" color="#fff">
                  {maturitySummary.next_stage_indicator}
                </Typography>
                <Typography color="#e6f0d3" fontSize={13}>
                  {maturitySummary.next_stage_name}
                </Typography>
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flex={1}
                minWidth={0}
              >
                {maturitySummary.stagewise.map((stage, index) => {
                  const isActive =
                    maturitySummary.next_stage_name === stage.stage_name;

                  return (
                    <React.Fragment key={stage._id}>
                      <Stack alignItems="center" gap={0.25} flex={1}>
                        <Typography className="stage-label" noWrap>
                          Stage {stage.stage_number}
                        </Typography>
                        <Stack
                          className={`stage-num ${isActive ? 'active' : ''}`}
                          alignItems="center"
                          justifyContent="center"
                        >
                          {stage.stage_number}
                        </Stack>
                      </Stack>
                      {index < maturitySummary.stagewise.length - 1 && (
                        <Box component="span" className="stage-arrow">
                          ›
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
              </Stack>
            </Stack>

            {/* Status */}
            <Stack direction="row" alignItems="center" gap={1}>
              <TrendingUpIcon sx={{ fontSize: 18, color: '#a3d45a' }} />
              <Typography className="text-green" fontSize={13}>
                {maturitySummary.status}
              </Typography>
            </Stack>
          </CardContent>

          {!minimize && (
            <CardActions
              sx={{
                px: { xs: 2, sm: 2.5 },
                py: 1.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography className="text-muted">
                Track your organisation's AI governance and risk management
                progress.
              </Typography>
            </CardActions>
          )}
        </>
      )}
    </Card>
  );
};

export default AIMaturityAssessment;
